import { ChevronDown, RotateCcw, SlidersHorizontal } from "lucide-react";
import { type ReactNode, useState } from "react";
import { cn } from "@/lib/utils";
import { FilterBar } from "./filter-bar";

interface FilterPanelProps {
  /** Total number of selected values across all filters — shown as a count badge. */
  activeCount: number;
  /**
   * One short string per active filter (e.g. "Status: Active"), rendered as
   * chips in the header while the panel is collapsed so selections stay visible.
   */
  summary?: string[];
  /** Filter rows (`<FilterRow>…`). Wrapped in a `<FilterBar>` internally. */
  children: ReactNode;
  /** Whether the panel starts expanded. Defaults to closed. */
  defaultOpen?: boolean;
  /**
   * Clear every filter (and search) back to defaults. When provided, a "Reset"
   * button shows in the header — enabled only while `canReset` is true.
   */
  onReset?: () => void;
  /** Whether there is anything to reset (usually `activeCount > 0 || search`). */
  canReset?: boolean;
}

/**
 * Collapsible "Filters" panel: a header toggle (icon + count badge + a summary
 * of the active filters when collapsed) over the filter rows. Collapsing frees
 * vertical space while keeping the current selection glanceable. Shared by every
 * filtered table so the affordance stays consistent.
 */
export function FilterPanel({
  activeCount,
  summary = [],
  children,
  defaultOpen = false,
  onReset,
  canReset = false,
}: FilterPanelProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex w-full items-start gap-2 px-3 py-2">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="flex min-w-0 flex-1 items-start gap-2 text-left text-sm font-medium text-foreground"
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="flex shrink-0 items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
              Filters
              {activeCount > 0 && (
                <span className="inline-flex items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground">
                  {activeCount}
                </span>
              )}
            </span>
            {!open &&
              summary.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center whitespace-nowrap rounded-md border bg-muted/40 px-1.5 py-0.5 text-xs font-normal text-muted-foreground"
                >
                  {s}
                </span>
              ))}
          </div>
          <ChevronDown
            className={cn(
              "mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform",
              open && "rotate-180",
            )}
          />
        </button>
        {onReset && (
          // Always present when the page supports reset, so the control is
          // consistent across pages; disabled when there's nothing to reset
          // (e.g. a board with no default filters, before you pick any).
          <button
            type="button"
            onClick={onReset}
            disabled={!canReset}
            className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 font-medium text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
          >
            <RotateCcw className="size-3" />
            Reset
          </button>
        )}
      </div>
      {open && (
        <div className="border-t px-3 py-2.5">
          <FilterBar>{children}</FilterBar>
        </div>
      )}
    </div>
  );
}

/** Map selected filter values back to their human labels for the collapsed summary. */
export function selectedLabels(
  options: { value: string; label: string }[],
  values: string[],
): string {
  return values.map((v) => options.find((o) => o.value === v)?.label ?? v).join(", ");
}
